import smh, { SMH } from '@/renderer/scripts/smh';
import i18n, { i18n_t } from '@/locales/i18n';
import store from '@/renderer/store/index';
import user from '@/renderer/scripts/utils/user';
import { checkTransBeforeQuit } from '@/renderer/scripts/utils/transmission/helper';
import tips from '@/renderer/scripts/utils/tips';
import router from '@/renderer/router';
import uploadTaskManager from '@/renderer/scripts/utils/upload';
import downloadTaskManager from '@/renderer/scripts/utils/download';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';

/**
 * 切换逻辑：先尝试用api登录目标企业，如果后端报错则无法切换；没报错则需要把当前用户的企业信息切换（libraryId、organizationId、expired、editionFlag、editionConfig）
 */

// 切换进企业时需要api
const loginToOrg = async (organizationId: number): Promise<boolean> => {
  let result = false;
  try {
    await smh.userAPI.loginOrg({
      organizationId,
    });
    result = true;
  } catch (e: any) {
    tips.error(e);
    result = false;
  }
  return result;
};

// api相关
const switchOrgFn = async (orgInfo: SMH.OrgItem) => {
  const { libraryId, id: organizationId, expired } = orgInfo;
  // 先判断能否登入该企业
  const canLoginToOrg = await loginToOrg(organizationId);
  if (!canLoginToOrg) {
    user.switchOrgFailed();
    return false;
  }

  // 全局通知要开始切换企业
  user.switchOrgStarted();

  // 切换新的企业信息
  const editionConfig = orgInfo?.extensionData?.editionConfig;
  const editionFlag = editionConfig?.editionFlag || 'enterprise';
  const orgChannel = orgInfo?.extensionData?.channelFlag;

  // 切换后会主动查询企业是否过期，所以此处不传expired
  await user.setOrgAndLogin({
    organizationId,
    libraryId,
    editionFlag,
    editionConfig,
    orgChannel,
  });
  await store.dispatch('getUserBaseInfo');
  await store.dispatch('GetOrgInfo', organizationId);
  store.commit('setOrgSwitchFlag');
  // 全局通知切换完成
  user.switchOrgFinished();

  // 切换身份后查询有无个人空间
  await store.dispatch('checkPersonalSpace');
  // 切换身份后查询权限列表
  await store.dispatch('GetRoles');
  // 切换身份后查询企业用量
  await store.dispatch('GetOrgSpace');
  return true;
};
const switchOrgAction = async (orgInfo: SMH.OrgItem) => {
  const switchSuccessful = await switchOrgFn(orgInfo);
  if (!switchSuccessful) return;
  // 切换成功后处理路由跳转
  const routeName = router.currentRoute.name as string;
  // 如果企业已经过期跳转至过期页面
  if (orgInfo.expired) {
    dispatchRouter({
      name: 'teamExpired',
    });
    tips.closeAll();
    return;
  }
  if (orgInfo.ipLimitEnabled) {
    tips.closeAll();
    return;
  }
  // 外链页、分享页保持不重定向
  if (['invitation', 'share'].includes(routeName)) return;

  // 如果是在我的工作台(移动端最近使用路由)切换身份，需要刷新页面
  if (['workbench', 'recentUse'].includes(routeName)) {
    window.location.reload();
  }
  // 重定向到工作台
  dispatchRouter({
    name: store.getters.isMobileStyle ? 'recentUse' : 'workbench',
  });
};

// 对外提供的切换方法
const switchOrg = async (orgInfo: SMH.OrgItem, beforeSwitch?: Function) => {
  checkTransBeforeQuit({
    type: 'switchOrg',
    className: 'switch-org-confirm-dialog',
    confirmFn: () => {
      tips.loading(i18n_t('正在切换企业,请稍后'), 0);
      beforeSwitch && beforeSwitch();
      // 切换企业需取消所有任务
      uploadTaskManager.cancel();
      downloadTaskManager.cancel();
      switchOrgAction(orgInfo);
    },
    cancelFn: () => {
      tips.closeAll();
    },
    noRunningTaskFn: () => {
      tips.loading(i18n_t('正在切换企业,请稍后'), 0);
      beforeSwitch && beforeSwitch();
      switchOrgAction(orgInfo);
    },
  });
};
export { switchOrgFn, switchOrg };
